/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from "lodash";
import {
  AllProgressCheckStatuses,
  CPDActivityType,
  CompetenceActivityClass,
  EvidenceAttachment,
  EvidenceDefinition,
  EvidencePart,
  EvidenceStandardTag,
  FilledInBy,
  IEvidence,
  IEvidenceDraft,
  IMandatoryStandard,
  INewDeepLinkRenfrewEvidence,
  INewTimelineEvidence,
  IUser,
  ProgramData,
  ProgramSkill,
  ProgressCheck,
  LinkedPartType,
  SkillsMultiSelectSelectedValue,
  FieldType,
} from "@interfaces/Interfaces";
import { FileContent } from "use-file-picker";
import { format, isAfter, isSameDay, isValid, parseISO, subMonths } from "date-fns";
import { IAttachmentProgress } from "@interfaces/Components";
import * as ProgramUtils from "@utils/programUtils";
import { Loadable } from "recoil";
import { EvidenceSearchFilter } from "@state/FilterState";
import moment from "moment";
import { escapeCSV } from "@utils/stringUtils";
import { PROGRAM_IDS } from "@constants/programs";

/**
 * Checks whether a user's selected attachment already exists before adding it to the upload array
 * @param plainFiles - An array of the selected file(s) metadata
 * @param filesContent - An array of the selected file(s) contents
 * @param attachments - The current attachments for the evidence
 * @param attachmentsToAdd - The attachments the user wants to add to the evidence
 * @returns The processed arrays of attachments
 */
export function addAttachmentToUploadArray(
  plainFiles: File[],
  filesContent: FileContent[],
  attachments: EvidenceAttachment[],
  attachmentsToAdd: EvidenceAttachment[]
): { attachments: EvidenceAttachment[]; toAdd: EvidenceAttachment[] } {
  const _attachments = _.cloneDeep(attachments);
  const toAdd = _.cloneDeep(attachmentsToAdd);

  for (let i = 0; i < plainFiles.length; i++) {
    const file = {
      name: plainFiles[i].name,
      type: plainFiles[i].type,
      size: plainFiles[i].size,
      content: filesContent[i].content,
      lastModified: plainFiles[i].lastModified,
    };

    const fileExists = _.findIndex(_attachments, (item) => item.name === file.name) > -1;
    const adding = _.findIndex(toAdd, (item) => item.name === file.name) > -1;

    if (!fileExists && !adding) {
      _attachments.push(file);
      toAdd.push(file);
    }
  }

  return {
    attachments: _attachments,
    toAdd,
  };
}

/**
 * Inserts a title into the evidenceJSON of a piece of evidence using the competence name
 * @param evidenceJSON - The evidence to insert
 * @param competence - The current progress check competence
 * @returns The evidenceJSON object with the added Title field
 */
export function addTitleToProgressCheck(evidenceJSON: any, competence: CompetenceActivityClass): any {
  const object = _.cloneDeep(evidenceJSON);
  const title = competence.Name;

  const newObject = _.extend(object, { Title: title });

  return newObject;
}

export function addSkillTitle(evidenceJSON: any, skill: string): any {
  const object = _.cloneDeep(evidenceJSON);
  const title = skill;

  const newObject = _.extend(object, { Title: title });

  return newObject;
}

export function addWitnessSimulationTitle(evidenceJSON: any): any {
  const object = _.cloneDeep(evidenceJSON);

  const newObject = _.extend(object, { Title: "Witnessed Intubation Simulation" });

  return newObject;
}

/**
 * Adds the the required skill to a piece of evidence for the chosen competence
 * @param evidenceJSON - The evidence to insert skill into
 * @param skill - The required skill to add
 * @param competence - The chosen competence
 * @returns The evidenceJSON item with the required skill inserted
 */
export function addRequiredSkill(evidenceJSON: any, skill: ProgramSkill, competence: CompetenceActivityClass): any {
  const evidenceDefinitions = competence["Evidence Definitions"][0];
  const evidenceParts = evidenceDefinitions?.["Evidence parts"] || [];
  const evidencePartWithSkills = evidenceParts.filter((item: EvidencePart) => item.Skills && item.Skills.length > 0)[0];

  const object = _.cloneDeep(evidenceJSON);
  const key = evidencePartWithSkills.Name;
  const newObject = _.extend(object, { [key]: [skill.Name] });

  return newObject;
}

export function addSelectedStandard(evidenceJSON: any, selectedStandard: IMandatoryStandard): any {
  const object = _.cloneDeep(evidenceJSON);

  const newObject = _.extend(object, { Comps: [{ id: selectedStandard.id, Name: selectedStandard.name }], LOs: [] });

  return newObject;
}

export function calculateTotalProgress(progressData: IAttachmentProgress[]): number {
  const percentages = progressData.map((item) => item.percentage);

  const progress = percentages.reduce((a, b) => a + b, 0) / (percentages.length * 100);

  return progress;
}

export function canEdit(
  evidence: IEvidence,
  userPrograms: ProgramData[],
  allProgressCheckData: Loadable<AllProgressCheckStatuses[] | null>,
  progressCheckLocked: boolean = false,
  allEvidence?: IEvidence[],
  currentProgramCycle?: number
) {
  if (!evidence.evidenceJSON) return true; // if no evidenceJSON, it's a personal evidence

  if (progressCheckLocked) return false; // if progress check is locked, can't edit

  const parsedEvidence = evidence.evidenceJSON;

  const evidenceLocked = parsedEvidence.programInfo.locked;
  const witnessEvidenceApproved = parsedEvidence.programInfo.witnessSimulationApproval;
  const evidenceCycle = parsedEvidence.programInfo.programCycle;

  if (evidenceLocked) return false;
  if (witnessEvidenceApproved) return false;
  if (evidenceCycle && currentProgramCycle && evidenceCycle !== currentProgramCycle) return false;

  const approvalState = parsedEvidence.programInfo?.approvalState;
  if (approvalState === "Approved" || approvalState === "Rejected") return false;

  const program = userPrograms.find((program) => program.ID === parsedEvidence.programInfo?.programID);
  const pc = program?.ProgressChecks?.find(
    (progressCheck) => progressCheck.ID === parsedEvidence.programInfo.progressCheckID
  );
  let competences: CompetenceActivityClass[] = [];
  if (program) {
    if (ProgramUtils.shouldGetProgressCheckInfo(program.ID)) {
      competences = pc?.["Competence/Activity"] ?? [];
    } else {
      competences = program.CompetenceActivity ?? [];
    }
  }
  const competence = competences.find(
    (item: CompetenceActivityClass) => item.ID === parsedEvidence.programInfo?.progressCheckCompetenceID
  );

  const dataForProgram: AllProgressCheckStatuses = allProgressCheckData.contents?.find(
    (item: any): item is AllProgressCheckStatuses => item.programID === program?.ID
  );
  const dataForProgressCheck = dataForProgram?.pCs.find((item) => item.pCId === pc?.ID);

  if (program && ProgramUtils.isCSM(program.ID)) {
    return (!competence?.NoEdit || (competence?.NoEdit && evidence.draft)) && parsedEvidence.OnHoldReset !== 1;
  } else {
    return (
      !competence?.isWellbeingActivity &&
      competence?.FilledInBy !== FilledInBy.Mentor &&
      (!competence?.NoEdit || (competence?.NoEdit && evidence.draft)) &&
      parsedEvidence.OnHoldReset !== 1 &&
      ProgramUtils.canAddOrEditProgressCheckEvidence(
        dataForProgressCheck?.submissions,
        pc,
        allProgressCheckData.contents,
        allEvidence || [],
        program?.ID,
        program?.ProgressChecks
      )
    );
  }
}

/**
 * Checks if any fields have been filled in by the user to be able to save as a draft
 * @param currentEvidence - The evidence to process
 * @returns
 */
export function checkAnyFieldsFilled(currentEvidence: IEvidence): boolean {
  return Object.keys(currentEvidence).length > 0;
}

/**
 * Checks whether this program evidence has a title field
 * @param evidenceJSON - The evidenceJSON field of the evidence
 * @returns
 */
export function checkEvidenceJSONHasTitle(evidenceJSON: any): boolean {
  return evidenceJSON.Title !== undefined;
}

export function checkIfSomeEvidenceAddedForComp(evidence: IEvidence[], comp: CompetenceActivityClass) {
  if (!evidence.length) return false;

  let res = false;

  // check if evidence is added
  for (const e of evidence) {
    const parsedEvidence = e.evidenceJSON;
    if (parsedEvidence.programInfo.progressCheckCompetenceID === comp.ID) {
      res = true;
    }
  }

  // check if los are added
  if (comp["Learning Objective"]) {
    for (const lo of comp["Learning Objective"]) {
      const loFound = evidence.some((e) => {
        let predicateResult = false;
        const parsedEvidence = e.evidenceJSON;
        if (parsedEvidence["Comp Selector"]) {
          Array.from(parsedEvidence["Comp Selector"]).forEach((it: any) => {
            it.selectedOutcomes.forEach((outcome: any) => {
              if (outcome.id === lo.ID) {
                predicateResult = true;
              }
            });
          });
        }
        if (parsedEvidence["LearningOutcomes"]) {
          Array.from(parsedEvidence["LearningOutcomes"]).forEach((outcome: any) => {
            if (outcome.ID === lo.ID) {
              predicateResult = true;
            }
          });
        }

        return predicateResult;
      });

      if (loFound) {
        res = true;
      }
    }
  }

  return res;
}

// TODO this is also used in CustomProgressCheckButton - consider putting in ProgramUtils.ts instead. comps count is also done in CustomProgressCheckEvidenceButton.tsx
export function calculateCompsDone(programEvidence: IEvidence[], progressCheck: ProgressCheck) {
  if (!programEvidence) {
    console.log("programEvidence is undefined");
    return 0;
  }
  const filteredEvidence = programEvidence.filter((e) => {
    const parsed = e.evidenceJSON;
    const onHoldReset = parsed.OnHoldReset ?? 0;
    if (!e.draft && onHoldReset === 0 && progressCheck.ID === parsed.programInfo.progressCheckID) {
      return true;
    }
  });

  let count = 0;
  progressCheck["Competence/Activity"].forEach((comp) => {
    if (comp["Number Evidences required"] === 0) return;

    const requiredNumber = comp["Number Evidences required"] ?? 0;
    const evidenceForComp = filteredEvidence.filter((e) => {
      const parsed = e.evidenceJSON;
      return parsed.programInfo.progressCheckCompetenceID == comp.ID;
    });
    if (evidenceForComp.length) {
      if (comp.MultipleAllowed) {
        if (evidenceForComp.length > 0) count += 1;
      } else {
        if (evidenceForComp.length >= requiredNumber) count += 1;
      }
    }
  });

  return count;
}

export function getProgramEvidenceAttachmentsLength(evidence: IEvidence): number {
  let result = 0;

  if (evidence.evidenceJSON) {
    const object = evidence.evidenceJSON;

    const values = Object.values(object);

    const attachments = values.filter((value) => value && typeof value === "object" && "attachments" in value);

    const attachmentsArray = [];

    if (attachments.length > 0) {
      attachmentsArray.push(
        ...attachments.map((attachment: any) => attachment.attachments.map((item: any) => ({ ...item })))
      );
    }

    const flattenedAttachments = attachmentsArray.flat();

    result = flattenedAttachments.length;
  }

  return result;
}

export function hasReachedEvidenceCount(
  evidence: IEvidence[],
  comp: CompetenceActivityClass
): "NONE" | "IN_PROGRESS" | "COMPLETE" {
  const filteredEvidence: IEvidence[] = evidence.filter((evidence: IEvidence) => {
    const evidenceJSON = evidence.evidenceJSON;
    return !evidenceJSON.OnHoldReset && !evidence.draft && evidenceJSON.programInfo?.approvalState !== "Rejected";
  });

  let evidenceCount = 0;
  for (const e of filteredEvidence) {
    const parsedEvidence = e.evidenceJSON;
    if (parsedEvidence.programInfo.progressCheckCompetenceID === comp.ID) {
      evidenceCount += 1;
    }
  }

  if (evidenceCount === 0) {
    return "NONE";
  } else if (evidenceCount >= (comp["Number Evidences required"] ?? 1)) {
    return "COMPLETE";
  } else {
    return "IN_PROGRESS";
  }
}

export function hasLearningOutcomes(
  evidence: IEvidence[],
  comp: CompetenceActivityClass
): "NONE" | "IN_PROGRESS" | "COMPLETE_REQUIRED" | "COMPLETE_ALL" {
  const filteredEvidence: IEvidence[] = evidence.filter((evidence: IEvidence) => {
    const evidenceJSON = evidence.evidenceJSON;
    return !evidenceJSON.OnHoldReset && !evidence.draft;
  });

  const totalNumberOfLearningOutcomes = comp["Learning Objective"]?.length ?? 0;

  const losRequired: string[] = []; // map of ids
  comp["Learning Objective"]?.forEach((currentLearningOutcome) => {
    const loFound = losRequired.find((lo: string | any) => lo.ID === currentLearningOutcome.ID);
    if (currentLearningOutcome.NumberRequired && !loFound) {
      losRequired.push(currentLearningOutcome.ID);
    }
  });

  let losDone = 0;
  const requiredLosDone: string[] = [];
  comp["Learning Objective"]?.forEach((lo) => {
    const numRequired = !lo.NumberRequired || lo.NumberRequired === 0 ? 1 : lo.NumberRequired;
    let countOfLearningOutcomesDone = 0;
    filteredEvidence.forEach((e) => {
      const parsedEvidence = e.evidenceJSON;
      if (parsedEvidence["Comp Selector"]) {
        Array.from(parsedEvidence["Comp Selector"]).forEach((it: any) => {
          it.selectedOutcomes.forEach((outcome: any) => {
            if (outcome.id === lo.ID) {
              countOfLearningOutcomesDone += 1;
            }
          });
        });
      }
      if (parsedEvidence["LearningOutcomes"]) {
        Array.from(parsedEvidence["LearningOutcomes"]).forEach((outcome: any) => {
          if (outcome.ID === lo.ID) {
            countOfLearningOutcomesDone += 1;
          }
        });
      }
      if (parsedEvidence["LearningObjectivesTickboxes"]) {
        Array.from(parsedEvidence["LearningObjectivesTickboxes"].selectedOutcomes).forEach((outcome: any) => {
          if (outcome.id === lo.ID) {
            countOfLearningOutcomesDone += 1;
          }
        });
      }
    });

    if (countOfLearningOutcomesDone >= numRequired) {
      losDone += 1;

      if (numRequired > 0) {
        requiredLosDone.push(lo.ID);
      }
    }
  });

  if (losDone === 0) {
    return "NONE";
  } else if (losDone === totalNumberOfLearningOutcomes) {
    return "COMPLETE_ALL";
  } else if (losRequired.every((it) => requiredLosDone.includes(it))) {
    return "COMPLETE_REQUIRED";
  } else {
    return "IN_PROGRESS";
  }
}

export function checkIfEvidencePartIsRequired(
  evidencePart: EvidencePart,
  evidenceJSON: any,
  parentEvidencePart: EvidencePart | undefined = undefined
): boolean {
  let required = true;

  if (evidencePart.Skills && evidencePart.Skills.length > 0) {
    required = checkLinkedPartIsRequiredSkill(evidencePart, evidenceJSON);
  } else if (evidencePart["Linked Part trigger"]) {
    required = checkLinkedPartIsRequired(evidencePart, evidenceJSON, parentEvidencePart);
  }

  return required;
}

// returns true if evidence parts contains a trigger (and it's been triggered), returns false otherwise // TODO replace
export const evidencePartTriggerUsed = (evidenceParts: EvidencePart[], evidenceJSON: any) => {
  let triggerObj: null | { trigger: string; evidenceKey: string } = null;
  for (const part of evidenceParts) {
    if (part["Progress Check Submission Trigger"]) {
      triggerObj = { trigger: part["Progress Check Submission Trigger"], evidenceKey: part.Name };
      break;
    }
  }

  if (triggerObj && evidenceJSON[triggerObj.evidenceKey] === triggerObj.trigger) {
    return true;
  }

  return false;
};

// returns true if evidence parts contains a trigger, returns false otherwise // TODO replace
export const evidencePartTriggerFound = (evidenceParts: EvidencePart[]) => {
  let result = false;
  for (const part of evidenceParts) {
    if (part["Progress Check Submission Trigger"] && !part.FilledInBySupervisor) {
      result = true;
    }
  }

  return result;
};

export function checkLinkedPartIsRequiredSkill(item: EvidencePart, evidenceJSON: any): boolean {
  let required = true;

  if (item["Linked Part"] && item["Linked Part"].length > 0) {
    const linkedPart = item["Linked Part"][0];
    const evidencePart = evidenceJSON[linkedPart.Name];
    const skill = item.Skills?.map((_item) => _item.Name)[0] ?? "";

    required = evidencePart?.includes(skill) || false;
  }

  return required;
}

export function checkLinkedPartIsRequired(
  item: EvidencePart,
  evidenceJSON: any,
  parentEvidencePart: EvidencePart | undefined = undefined
): boolean {
  let required = true;

  const hasLinkedParts = item["Linked Part"] && item["Linked Part"].length > 0;

  if (parentEvidencePart) {
    const triggers = item["Linked Part trigger"]
      ? item["Linked Part trigger"].split("\n").map((value) => value.trim())
      : [];
    const selectedValue = evidenceJSON[parentEvidencePart.Name];

    if (typeof selectedValue === "string" && triggers.includes(selectedValue.trim())) {
      return true;
    } else if (
      Array.isArray(selectedValue) &&
      typeof selectedValue[0] === "string" &&
      triggers.some((trigger) => selectedValue.some((value) => value?.trim() === trigger))
    ) {
      return true;
    }

    return false;
  }

  if (hasLinkedParts) {
    const linkedPart = item["Linked Part"]![0];
    const evidencePart = evidenceJSON[linkedPart.Name];
    const triggers = item["Linked Part trigger"]
      ? item["Linked Part trigger"].split("\n").map((value) => value.trim())
      : [];

    required = triggers.includes(evidencePart);
  }

  return required;
}

export function checkNOSSelectIsFilled(evidenceJSON: any): boolean {
  if (evidenceJSON.Comps) {
    return evidenceJSON.Comps.length > 0;
  }

  return false;
}

export function checkLearningOutcomesIsFilled(evidenceJSON: any): boolean {
  if (evidenceJSON.LearningOutcomes) {
    return evidenceJSON.LearningOutcomes.length > 0;
  }

  return false;
}

export function checkLOsMultiSelectForNQPIsFilled(evidenceJSON: any): boolean {
  if (evidenceJSON.LearningObjectivesTickboxes?.selectedOutcomes) {
    return evidenceJSON.LearningObjectivesTickboxes.selectedOutcomes.length > 0;
  }

  return false;
}

/**
 * Removes any non-required evidence parts from a piece of evidence that are empty
 * @param json - The evidence to process
 * @param evidenceParts - The evidence parts for the competence/ skill
 * @returns The evidence with the non-required parts removed
 */
export function deleteNonRequiredEvidencePart(json: object, evidenceParts: EvidencePart[]) {
  const object: object = _.cloneDeep(json);

  const isPartRequired = (part: EvidencePart): boolean => {
    if (part.Skills && part.Skills.length > 0) {
      return checkLinkedPartIsRequiredSkill(part, object);
    }

    if (part["Linked Part trigger"]) {
      const parentPart = evidenceParts.find((evidencePart: EvidencePart) =>
        evidencePart["Linked Part"]?.find((linkedPart: EvidencePart) => linkedPart.ID === part.ID)
      );

      return checkLinkedPartIsRequired(part, object, parentPart);
    }

    return true;
  };

  for (const part of evidenceParts) {
    const sharesNameWithRequiredPart = evidenceParts.some(
      (other) => other.ID !== part.ID && other.Name === part.Name && isPartRequired(other)
    );

    if (!isPartRequired(part) && !sharesNameWithRequiredPart && typeof object === "object") {
      delete object[part.Name as keyof object];
    }
  }

  return object;
}

export function filterDrafts(
  sortedDrafts: IEvidenceDraft[],
  selectedStandards: string[],
  selectedCustomTags: string[],
  selectedPrograms: string[],
  filterHCPC: boolean
): IEvidenceDraft[] {
  if (selectedStandards.length === 0 && selectedCustomTags.length === 0 && selectedPrograms.length === 0) {
    let copy = _.cloneDeep(sortedDrafts);

    if (filterHCPC) {
      copy = copy.filter((draft: IEvidenceDraft) => draft.evidence.addToHCPCAudit);
    }

    return copy;
  } else {
    let copy = _.cloneDeep(sortedDrafts) || [];

    if (filterHCPC) {
      copy = copy.filter((draft: IEvidenceDraft) => draft.evidence.addToHCPCAudit);
    }

    copy = copy?.filter((item) => {
      const standardTags = item.evidence.standardTags?.map((tag) => tag.tag) || [];
      const customTags = item.evidence.customTags?.map((tag) => tag.tag) || [];
      const evidenceJSON = item.evidence.evidenceJSON;
      const programName = evidenceJSON?.programInfo?.program || "";

      const standardIncluded = selectedStandards.every((tag) => standardTags.includes(tag));
      const customIncluded = selectedCustomTags.every((tag) => customTags.includes(tag));
      const programIncluded = selectedPrograms.includes(programName);

      if (selectedPrograms.length > 0 && selectedCustomTags.length > 0 && selectedStandards.length > 0) {
        return programIncluded && standardIncluded && customIncluded;
      } else if (selectedPrograms.length > 0 && selectedCustomTags.length > 0) {
        return programIncluded && customIncluded;
      } else if (selectedPrograms.length > 0 && selectedStandards.length > 0) {
        return programIncluded && standardIncluded;
      } else if (selectedStandards.length > 0 && selectedCustomTags.length > 0) {
        return standardIncluded && customIncluded;
      } else if (selectedPrograms.length > 0) {
        return programIncluded;
      } else if (selectedStandards.length > 0) {
        return standardIncluded;
      } else if (selectedCustomTags.length > 0) {
        return customIncluded;
      } else {
        return false;
      }
    });

    return copy;
  }
}

export function filterEvidence(
  sortedEvidence: IEvidence[],
  selectedStandards: string[],
  selectedCustomTags: string[],
  selectedPrograms: string[],
  filterHCPC: boolean,
  filterDrafts: boolean,
  filterPrivate: boolean,
  filterGeneral: boolean
): IEvidence[] {
  if (selectedStandards.length === 0 && selectedCustomTags.length === 0 && selectedPrograms.length === 0) {
    let copy = _.cloneDeep(sortedEvidence);

    if (filterDrafts) {
      copy = copy.filter((evidence: IEvidence) => evidence.draft === true);
    }

    if (filterPrivate) {
      copy = copy.filter((evidence: IEvidence) => evidence.private === true);
    }

    if (filterHCPC) {
      copy = copy.filter((evidence: IEvidence) => evidence.addToHCPCAudit);
    }

    if (filterGeneral) {
      copy = copy.filter((evidence: IEvidence) => !evidence.evidenceJSON);
    }

    return copy;
  } else {
    let copy = _.cloneDeep(sortedEvidence) || [];

    if (filterDrafts) {
      copy = copy.filter((evidence: IEvidence) => evidence.draft === true);
    }

    if (filterPrivate) {
      copy = copy.filter((evidence: IEvidence) => evidence.private === true);
    }

    if (filterHCPC) {
      copy = copy.filter((evidence: IEvidence) => evidence.addToHCPCAudit);
    }

    copy = copy?.filter((item) => {
      if (filterGeneral) {
        if (!item.evidenceJSON) return true;
      }

      const standardTags = item.standardTags?.map((tag) => tag.tag) || [];
      const customTags = item.customTags?.map((tag) => tag.tag) || [];
      const evidenceJSON = item.evidenceJSON;
      const programName = evidenceJSON?.programInfo?.program || "";

      const standardIncluded = selectedStandards.every((tag) => standardTags.includes(tag));
      const customIncluded = selectedCustomTags.every((tag) => customTags.includes(tag));
      const programIncluded = selectedPrograms.includes(programName);

      if (selectedPrograms.length > 0 && selectedCustomTags.length > 0 && selectedStandards.length > 0) {
        return programIncluded && standardIncluded && customIncluded;
      } else if (selectedPrograms.length > 0 && selectedCustomTags.length > 0) {
        return programIncluded && customIncluded;
      } else if (selectedPrograms.length > 0 && selectedStandards.length > 0) {
        return programIncluded && standardIncluded;
      } else if (selectedStandards.length > 0 && selectedCustomTags.length > 0) {
        return standardIncluded && customIncluded;
      } else if (selectedPrograms.length > 0) {
        return programIncluded;
      } else if (selectedStandards.length > 0) {
        return standardIncluded;
      } else if (selectedCustomTags.length > 0) {
        return customIncluded;
      } else {
        return false;
      }
    });

    return copy;
  }
}

export function filterStandardTags(
  search: string,
  standardTags: EvidenceStandardTag[],
  mandatoryStandards: any[] | undefined,
  optionalStandards: any[] | undefined
): { mandatory: any[] | undefined; optional: any[] | undefined } {
  const addedTags = standardTags.map((item) => item.tag);

  const mandatory = mandatoryStandards?.filter((item) => {
    return (
      !addedTags.includes(item.Code) &&
      (item.Code.toLowerCase().includes(search.toLowerCase()) ||
        (item.Description && item.Description.toLowerCase().includes(search.toLowerCase())))
    );
  });

  const optional = optionalStandards?.filter((item) => {
    return (
      !addedTags.includes(item.Code) &&
      (item.Code.toLowerCase().includes(search.toLowerCase()) ||
        (item.Description && item.Description.toLowerCase().includes(search.toLowerCase())))
    );
  });

  return { mandatory, optional };
}

export function formatProgramName(programName: string) {
  return programName.replace(/\s+/gim, "-").toLowerCase();
}

export function getLinkedChildParts(evidencePart: EvidencePart): EvidencePart[] | undefined {
  if (evidencePart["Linked Part"]) {
    const array = evidencePart["Linked Part"].filter((item) => item["Linked Part type"] === LinkedPartType.Child);

    if (array.length > 0) {
      return array;
    }
  }

  return undefined;
}

export function getLinkedSiblingParts(evidencePart: EvidencePart) {
  if (evidencePart["Linked Part"]) {
    const array = evidencePart["Linked Part"].filter((item) => item["Linked Part type"] === LinkedPartType.Sibling);

    if (array.length > 0) {
      return array;
    }
  }

  return [];
}

/**
 *
 * @returns An array of the default activity types for personal evidence
 */
export function getDefaultActivityTypes(): CPDActivityType[] {
  return [
    { type: "Formal education" },
    { type: "Professional activity" },
    { type: "Self-directed learning" },
    { type: "Work-based learning" },
    { type: "Other" },
  ];
}

export function getDraftsCount(evidence: IEvidence[] | undefined): number {
  if (evidence) {
    const draftEvidence = evidence.filter((item) => item.draft === true);

    return draftEvidence.length;
  }

  return 0;
}

export function getEvidenceParts(evidenceDefinition: EvidenceDefinition): EvidencePart[] {
  return evidenceDefinition["Evidence parts"] || evidenceDefinition["Evidence parts Mandatory"] || [];
}

export function flattenEvidenceParts(parts: EvidencePart[], seen: Set<string> = new Set()): EvidencePart[] {
  return parts.flatMap((part) => {
    if (seen.has(part.id)) return [];
    seen.add(part.id);
    return [part, ...flattenEvidenceParts(part["Linked Part"] ?? [], seen)];
  });
}

export function getSearchResultText(searchResults: IEvidence[]): string {
  if (searchResults) {
    return `${searchResults && searchResults.length} result${searchResults && searchResults.length !== 1 ? "s" : ""}`;
  }

  return "";
}

export function getStandardTagsForRole(
  roles: any,
  userRole: string,
  standards: any,
  initialEvidence: any
): {
  optionalRoles: any;
  mandatoryRoles: any;
  filteredOptional: any;
  filteredMandatory: any;
} {
  const roleObject = roles.find((item: any) => item.Name === userRole);

  const optional = roleObject?.OptionalRoles;
  const mandatory = roleObject?.MandatoryRoles;

  const mandatoryRoles = mandatory?.map((id: string) => standards.find((item: any) => item.id === id));
  const optionalRoles = optional?.map((id: string) => standards.find((item: any) => item.id === id));

  const initial: IEvidence | null = _.cloneDeep(initialEvidence);
  const codes = initial?.standardTags?.map((tag) => tag.tag) || [];

  const filteredOptional = _.filter(optionalRoles, (item) => !codes?.includes(item?.Code));
  const filteredMandatory = _.filter(mandatoryRoles, (item) => !codes?.includes(item?.Code));

  return {
    optionalRoles,
    mandatoryRoles,
    filteredOptional,
    filteredMandatory,
  };
}

export function handleNumberOfHours(input: string): string {
  let result = "";

  if (input.includes(":")) {
    const split = input.split(":");

    if (split[1].length === 0) {
      result = `${split[0]}:00`;
    } else if (split[1].length === 1) {
      result = `${split[0]}:0${split[1]}`;
    } else if (split[1].length === 2) {
      let hours = parseInt(split[0], 10);
      let minutes = parseInt(split[1], 10);

      if (minutes > 59) {
        hours += 1;
        minutes = minutes - 60;
      }

      if (hours === 100) {
        hours = 99;
        minutes = 59;
      }

      if (hours < 10) {
        if (minutes < 10) {
          result = `0${hours}:0${minutes}`;
        } else {
          result = `0${hours}:${minutes}`;
        }
      } else {
        if (minutes < 10) {
          result = `${hours}:0${minutes}`;
        } else {
          result = `${hours}:${minutes}`;
        }
      }
    }
  } else {
    if (input.length === 1) {
      result = `0${input}:00`;
    } else if (input.length === 2) {
      result = `${input}:00`;
    }
  }

  return result;
}

export function hasData(evidence: IEvidence[] | undefined): boolean {
  if (evidence) {
    return evidence.length > 0;
  }

  return false;
}

export function hideAuditTag(programID: string): boolean {
  return [PROGRAM_IDS.UOC_APPRENTICESHIP].includes(programID);
}

export function isBanner(evidencePart: EvidencePart): boolean {
  if (evidencePart["Field type"]) {
    return ["Banner"].includes(evidencePart["Field type"]);
  }

  return false;
}

export function isEvidenceTitleBlank(title: string | undefined): boolean {
  return typeof title === "undefined" || title.trim().length === 0;
}

export function isCompMultiSelect(evidencePart: EvidencePart): boolean {
  if (evidencePart["Field type"]) {
    return ["CompMultiSelectForLOs"].includes(evidencePart["Field type"]);
  }

  return false;
}

export function isNQPCompMultiSelect(evidencePart: EvidencePart): boolean {
  if (evidencePart["Field type"]) {
    return ["LOsMultiSelectForNQP"].includes(evidencePart["Field type"]);
  }

  return false;
}

export function isNOSSelect(evidencePart: EvidencePart): boolean {
  if (evidencePart["Field type"]) {
    return ["NOS Select"].includes(evidencePart["Field type"]);
  }

  return false;
}

export function isLearningOutcomesList(evidencePart: EvidencePart): boolean {
  if (evidencePart["Field type"]) {
    return ["LearningOutcomesList"].includes(evidencePart["Field type"]);
  }

  return false;
}

export function isSkillsSelect(evidencePart: EvidencePart): boolean {
  if (evidencePart["Field type"]) {
    return ["SkillsSelect"].includes(evidencePart["Field type"]);
  }

  return false;
}

export function getListOfValues(evidencePart: EvidencePart): string[] {
  return (
    evidencePart["List of values"]
      ?.split("\n")
      .map((item) => item.trim())
      .filter(Boolean) ?? []
  );
}

/** Form values for a Renfrew evidence type, keyed by the `evidencetype` deeplink param. */
export const RENFREW_DEEPLINK_EVIDENCE_TYPE_LABEL: Record<string, string> = {
  cpg: "CPG Learning Module",
  clinicalguide: "Clinical Guide",
  generalevidence: "General evidence",
};

/** Section to show in a Renfrew SkillsSelect for the selected evidence type. */
export const RENFREW_SKILL_SECTION_BY_TYPE: Record<string, string> = {
  "CPG Learning Module": "CPGs",
  "Clinical Guide": "Clinical Guides",
};

export function getRenfrewEvidenceDate(evidence: IEvidence): Date | undefined {
  const raw = evidence.evidenceJSON?.Date;

  if (!raw) {
    return undefined;
  }

  const date = new Date(raw);

  return isNaN(date.getTime()) ? undefined : date;
}

/**
 * Section to show in a Renfrew SkillsSelect for the selected evidence type.
 * CPG Learning Module lists CPGs skills; Clinical Guide lists Clinical Guides skills.
 */
export function getRenfrewSkillSectionForSelect(
  evidencePart?: EvidencePart,
  evidenceJSON?: Record<string, unknown>
): string | undefined {
  const triggers =
    evidencePart?.["Linked Part trigger"]
      ?.split("\n")
      .map((item) => item.trim())
      .filter(Boolean) ?? [];

  const selectedType = Object.values(evidenceJSON ?? {}).find(
    (value): value is string =>
      typeof value === "string" &&
      RENFREW_SKILL_SECTION_BY_TYPE[value.trim()] !== undefined &&
      (triggers.length === 0 || triggers.includes(value.trim()))
  );

  if (selectedType) {
    return RENFREW_SKILL_SECTION_BY_TYPE[selectedType.trim()];
  }

  const mappedTriggers = triggers.filter((trigger) => RENFREW_SKILL_SECTION_BY_TYPE[trigger]);

  if (mappedTriggers.length === 1) {
    return RENFREW_SKILL_SECTION_BY_TYPE[mappedTriggers[0]];
  }

  return undefined;
}

/**
 * Maps a Paramedic Folio Renfrew deeplink onto the program evidence form.
 * Query values are stored as given: `evidencetype` is `cpg`, `clinicalguide`, or `generalevidence`, and `duration` is the picker value (`1:30`).
 */
export function buildRenfrewDeeplinkEvidence(params: {
  evidenceParts: EvidencePart[];
  skills: ProgramSkill[];
  deeplink: INewDeepLinkRenfrewEvidence;
}) {
  const parts = flattenEvidenceParts(params.evidenceParts);
  const evidenceJSON: Record<string, string> = {};
  const title = params.deeplink.title?.trim();
  const evidenceTypeKey = params.deeplink.evidenceType?.trim().toLowerCase();
  const typeLabel = evidenceTypeKey ? RENFREW_DEEPLINK_EVIDENCE_TYPE_LABEL[evidenceTypeKey] : undefined;
  const duration = params.deeplink.duration?.trim() ?? "";
  const skillName = params.deeplink.skill?.trim();
  const section = typeLabel ? RENFREW_SKILL_SECTION_BY_TYPE[typeLabel] : undefined;

  if (title) {
    evidenceJSON.Title = title;
  }

  if (typeLabel) {
    const typePart = parts.find((part) => getListOfValues(part).includes(typeLabel));
    evidenceJSON[typePart?.Name ?? "Type"] = typeLabel;
  }

  if (duration) {
    const durationPart = parts.find((part) => part["Field type"] === FieldType.ActivityDuration);
    evidenceJSON[durationPart?.Name ?? "Activity duration"] = duration;
  }

  const preselectedSkill =
    skillName && evidenceTypeKey !== "generalevidence"
      ? params.skills.find((skill) => skill.Name.trim() === skillName && (!section || skill.Section === section))
      : undefined;

  if (skillName && evidenceTypeKey !== "generalevidence") {
    const skillPart = parts.find(
      (part) =>
        part["Field type"] === FieldType.SkillsSelect && !!typeLabel && part["Linked Part trigger"]?.includes(typeLabel)
    );
    evidenceJSON[skillPart?.Name ?? "SkillsSelect"] = preselectedSkill?.Name ?? skillName;
  }

  return {
    evidenceJSON,
    ...(title && { title }),
    ...(duration && { duration }),
    ...(preselectedSkill && { preselectedSkill }),
  };
}

/**
 * Payload for `evidence/add-program-evidence` that opens the Renfrew evidence form prefilled from a deeplink.
 */
export function buildRenfrewProgramEvidenceDeeplink(params: {
  program: ProgramData;
  user: IUser;
  deeplink: INewDeepLinkRenfrewEvidence;
}): Record<string, unknown> {
  const evidenceDefinitions = params.program["Evidence Definitions"] ?? [];
  const evidenceDefinition =
    evidenceDefinitions.find((item) => item.EvidenceSelectionType === "skills") ?? evidenceDefinitions[0];
  const competences = params.program.CompetenceActivity ?? [];
  const mandatoryStandards = ProgramUtils.getMandatoryStandards(params.user, params.program);
  const optionalStandards = ProgramUtils.getOptionalStandards(params.user, params.program);
  const skills = ProgramUtils.getSkillsForRole(params.user, params.program);
  const prefill = buildRenfrewDeeplinkEvidence({
    evidenceParts: evidenceDefinition ? getEvidenceParts(evidenceDefinition) : [],
    skills,
    deeplink: params.deeplink,
  });

  return {
    program: params.program,
    ...(competences.length > 0 && { competences }),
    ...(evidenceDefinition && { evidenceDefinitions: evidenceDefinition }),
    ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
    ...(optionalStandards.length > 0 && { optionalStandards }),
    ...(skills.length > 0 && { skills }),
    skill: null,
    evidenceJSON: prefill.evidenceJSON,
    prefilledNewEvidence: true,
    ...(prefill.preselectedSkill && { preselectedSkill: prefill.preselectedSkill }),
    ...(prefill.title && { preselectedTitle: prefill.title }),
    ...(prefill.duration && { preselectedDuration: prefill.duration }),
  };
}

/**
 * Maps timeline evidence onto program evidence parts using DeepLinkKey (Learning Loop style),
 * with field-type/name fallbacks when DeepLinkKey is not configured (e.g. Renfrew).
 */
export function mapTimelineEvidenceToProgramEvidenceJSON(
  evidenceParts: EvidencePart[],
  timelineEvidence: INewTimelineEvidence
): Record<string, any> {
  const parts = flattenEvidenceParts(evidenceParts);
  const deeplinkObject: Record<string, unknown> = {
    title: timelineEvidence.title,
    date: timelineEvidence.activityDate ?? timelineEvidence.date,
    description: timelineEvidence.activityDescription,
    attachments: timelineEvidence.pdfData,
  };

  const evidenceJSON = parts.reduce<Record<string, any>>((acc, part) => {
    if (!part.DeepLinkKey) {
      return acc;
    }

    const value = deeplinkObject[part.DeepLinkKey];
    if (value === undefined || value === null || value === "") {
      return acc;
    }

    return {
      ...acc,
      [part.Name]: mapDeepLinkObjectToEvidenceJSON(value, part),
    };
  }, {});

  if (timelineEvidence.title && evidenceJSON.Title === undefined) {
    const titleAlreadyMapped = parts.some(
      (part) => part.DeepLinkKey === "title" && evidenceJSON[part.Name] !== undefined
    );
    if (!titleAlreadyMapped) {
      evidenceJSON.Title = timelineEvidence.title;
    }
  }

  if (timelineEvidence.activityDescription) {
    const descriptionAlreadyMapped = parts.some(
      (part) => part.DeepLinkKey === "description" && evidenceJSON[part.Name] !== undefined
    );
    if (!descriptionAlreadyMapped) {
      const descriptionPart = parts.find(
        (part) =>
          part.Name === "Description" ||
          part.Name === "Comments" ||
          part.Name === "Activity description" ||
          part.Name.toLowerCase().includes("description")
      );
      if (descriptionPart && evidenceJSON[descriptionPart.Name] === undefined) {
        evidenceJSON[descriptionPart.Name] = mapDeepLinkObjectToEvidenceJSON(
          timelineEvidence.activityDescription,
          descriptionPart
        );
      }
    }
  }

  const activityDate = timelineEvidence.activityDate ?? timelineEvidence.date;
  if (activityDate) {
    const dateAlreadyMapped = parts.some(
      (part) => part.DeepLinkKey === "date" && evidenceJSON[part.Name] !== undefined
    );
    if (!dateAlreadyMapped) {
      const datePart = parts.find((part) => part["Field type"] === FieldType.Date || part.Name === "Date");
      if (datePart && evidenceJSON[datePart.Name] === undefined) {
        evidenceJSON[datePart.Name] = mapDeepLinkObjectToEvidenceJSON(activityDate, datePart);
      }
    }
  }

  if (timelineEvidence.pdfData?.pdf) {
    const attachmentsAlreadyMapped = parts.some(
      (part) =>
        (part.DeepLinkKey === "attachments" || part["Field type"] === FieldType.Attachments) &&
        evidenceJSON[part.Name] !== undefined
    );
    if (!attachmentsAlreadyMapped) {
      const attachmentsPart = parts.find((part) => part["Field type"] === FieldType.Attachments);
      if (attachmentsPart && evidenceJSON[attachmentsPart.Name] === undefined) {
        evidenceJSON[attachmentsPart.Name] = mapDeepLinkObjectToEvidenceJSON(timelineEvidence.pdfData, attachmentsPart);
      }
    }
  }

  return evidenceJSON;
}

/**
 * Payload for `evidence/add-program-evidence` that opens a Canada program form prefilled from timeline evidence.
 */
export function buildTimelineProgramEvidenceDeeplink(params: {
  program: ProgramData;
  user: IUser;
  timelineEvidence: INewTimelineEvidence;
}): Record<string, unknown> {
  const evidenceDefinitions = params.program["Evidence Definitions"] ?? [];
  const addProgramEvidenceType = ProgramUtils.getAddProgramEvidenceType(params.program.ID);
  const usesAdditionalEvidenceDefinitions = params.program.UsesAdditionalEvidenceDefinitions;
  const evidenceDefinition =
    addProgramEvidenceType.type === "skill"
      ? evidenceDefinitions.find((item) => item.EvidenceSelectionType === "skills") ?? evidenceDefinitions[0]
      : evidenceDefinitions[0];

  const competences = params.program.CompetenceActivity ?? [];
  const progressChecks = params.program.ProgressChecks ?? [];
  const mandatoryStandards = ProgramUtils.getMandatoryStandards(params.user, params.program);
  const optionalStandards = ProgramUtils.getOptionalStandards(params.user, params.program);
  const skills = ProgramUtils.getSkillsForRole(params.user, params.program);
  const evidenceParts = evidenceDefinition ? getEvidenceParts(evidenceDefinition) : [];
  const evidenceJSON = mapTimelineEvidenceToProgramEvidenceJSON(evidenceParts, params.timelineEvidence);

  const includeEvidenceDefinitions =
    !!evidenceDefinition &&
    (addProgramEvidenceType.type === "skill"
      ? !!usesAdditionalEvidenceDefinitions || evidenceDefinitions.length > 0
      : !usesAdditionalEvidenceDefinitions);

  return {
    program: params.program,
    ...(competences.length > 0 && { competences }),
    ...(includeEvidenceDefinitions && evidenceDefinition && { evidenceDefinitions: evidenceDefinition }),
    ...(addProgramEvidenceType.type !== "skill" && progressChecks.length > 0 && { progressChecks }),
    ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
    ...(optionalStandards.length > 0 && { optionalStandards }),
    ...(skills.length > 0 && { skills }),
    skill: null,
    evidenceJSON,
    prefilledNewEvidence: true,
    preselectedTitle: params.timelineEvidence.title,
    timelineReference: params.timelineEvidence.timelineReference,
    ...(params.timelineEvidence.pdfData && { pdfData: params.timelineEvidence.pdfData }),
  };
}

/**
 * Returns the parent part values (e.g. { Type: "CPG Learning Module" }) needed to show the SkillsSelect part
 * that lists the given skill. If more than one SkillsSelect part lists the skill, the one listing the most
 * skills from the same Section as the given skill is used.
 */
export function getTriggerValuesForSkill(
  evidenceParts: EvidencePart[],
  skill: ProgramSkill,
  skills: ProgramSkill[]
): Record<string, string> {
  const candidates = evidenceParts.flatMap((parentPart) =>
    (parentPart["Linked Part"] ?? [])
      .filter((linkedPart) => isSkillsSelect(linkedPart) && getListOfValues(linkedPart).includes(skill.Name.trim()))
      .map((linkedPart) => ({ parentPart, linkedPart }))
  );

  const match = _.maxBy(candidates, ({ linkedPart }) => {
    const listedSkills = getListOfValues(linkedPart);

    return skills.filter((item) => item.Section === skill.Section && listedSkills.includes(item.Name.trim())).length;
  });

  const trigger = match?.linkedPart["Linked Part trigger"]?.split("\n")[0]?.trim();

  return match && trigger ? { [match.parentPart.Name]: trigger } : {};
}

export function isSkillsMultiSelect(evidencePart: EvidencePart): boolean {
  if (evidencePart["Field type"]) {
    return ["SkillsMultiSelect"].includes(evidencePart["Field type"]);
  }

  return false;
}

export function titleHasNoData(evidenceParts: EvidencePart[]): boolean {
  const titlePart = evidenceParts.find((item) => item.Name === "Title");

  return titlePart == null;
}

const MONTHS_MAP: { [key: string]: number } = {
  "3 months": 3,
  "6 months": 6,
  "1 year": 12,
  "2 years": 24,
};

export function getActivityTypesCountFromEvidence(evidence: IEvidence[]): number {
  const activityTypes = evidence.filter((item) => item.activity?.type !== undefined).map((item) => item.activity!.type);

  return Array.from(new Set(activityTypes)).length;
}

export function getAddedToAuditCountFromEvidence(evidence: IEvidence[]): number {
  return evidence.filter((item) => item.addToHCPCAudit).length;
}

export function getFilteredEvidence(filters: EvidenceSearchFilter[], evidence: IEvidence[]): IEvidence[] {
  let filteredEvidence = structuredClone(evidence);

  const groupedFilters: { [type: string]: string[] } = filters.reduce((acc: any, filter) => {
    if (!acc[filter.type]) {
      acc[filter.type] = [];
    }
    acc[filter.type].push(filter.option);
    return acc;
  }, {});

  for (const filterType in groupedFilters) {
    const options = groupedFilters[filterType];

    switch (filterType) {
      case "General":
        filteredEvidence = filteredEvidence.filter((item: IEvidence) => {
          return options.some((option) => {
            switch (option) {
              case "Drafts":
                return item.draft;
              case "Tagged for audit":
                return item.addToHCPCAudit;
              case "Private":
                return item.private;
              case "Personal":
                return !item.evidenceJSON;
              default:
                return true;
            }
          });
        });
        break;
      case "Drafts":
        filteredEvidence = filteredEvidence.filter((item: IEvidence) => {
          return options.some((option) => {
            switch (option) {
              case "Include Drafts":
                return item.draft;
              case "Exclude Drafts":
                return !item.draft;
              default:
                return true;
            }
          });
        });
        break;
      case "Date Range":
        filteredEvidence = filteredEvidence.filter((item: IEvidence) => {
          return options.some((option) => {
            const months = MONTHS_MAP[option];
            if (months) {
              const startDate = subMonths(new Date(), months);
              const date = parseISO(item.date);
              return isSameDay(date, startDate) || isAfter(date, startDate);
            }
            return false;
          });
        });
        break;
      case "CPD Activity":
        filteredEvidence = filteredEvidence.filter((item: IEvidence) => {
          return options.some((option) => item.activity?.type === option);
        });
        break;
      case "Program Modules":
        filteredEvidence = filteredEvidence.filter((item: IEvidence) => {
          if (options.includes("Incident Portfolio")) {
            return options.some((option) => {
              return item.standardTags || item.evidenceJSON?.programInfo.programID === option;
            });
          }
          return options.some((option) => item.evidenceJSON?.programInfo.programID === option);
        });
        break;
      case "NOS":
        filteredEvidence = filteredEvidence.filter((item: IEvidence) => {
          return options.some((option) => item.standardTags?.some((tag) => tag.tag === option));
        });
        break;
      case "Custom Tags":
        filteredEvidence = filteredEvidence.filter((item: IEvidence) => {
          return options.some((option) => item.customTags?.some((tag) => tag.tag === option));
        });
        break;
    }
  }

  return filteredEvidence;
}

export function searchEvidence(searchText: string, evidence: IEvidence[]): IEvidence[] {
  const search = searchText.toLowerCase();

  if (!search || search.length === 0) return evidence;

  const searchEvidence = _.filter(evidence, (item: IEvidence) => {
    const evidenceJSON = item.evidenceJSON;

    const title: string = (item.title || evidenceJSON.Title || "").toLowerCase();
    const programName: string = (evidenceJSON?.programInfo?.program || "").toLowerCase();
    const competenceName: string = (evidenceJSON?.programInfo?.competence || "").toLowerCase();
    const progressCheckName: string = (evidenceJSON?.programInfo?.progressCheck || "").toLowerCase();
    const taskingType: string = (evidenceJSON?.["Tasking type or training"] || "").toLowerCase();
    const formattedDate: string = format(new Date(item.date), "d MMM yyyy").toLowerCase();
    const activityType: string = (item.activity?.type || "").toLowerCase();
    const skill: string = ((evidenceJSON && getEvidenceSkillKey(item)) ?? "").toLowerCase();
    const skillType: string = ((evidenceJSON && getEvidenceSkillTypeKey(item)) ?? "").toLowerCase();

    return (
      title.includes(search) ||
      programName.includes(search) ||
      competenceName.includes(search) ||
      progressCheckName.includes(search) ||
      taskingType.includes(search) ||
      formattedDate.includes(search) ||
      activityType.includes(search) ||
      skill.includes(search) ||
      skillType.includes(search)
    );
  });

  return searchEvidence;
}

export function getEvidenceFieldHeader(key: string): string {
  switch (key) {
    case "title":
      return "Title";
    case "date":
      return "Date of reflection";
    case "activityDate":
      return "Date of activity";
    case "activity":
      return "CPD Activity";
    case "numberOfHours":
      return "Activity duration";
    case "standardTags":
      return "NOS tags";
    case "customTags":
      return "Custom tags";
    case "attachments":
      return "Attachments";
    case "activityDescription":
      return "Activity description";
    case "reflections":
      return "Reflections";
    case "benefits":
      return "Benefits";
    case "objectives":
      return "Learning / Exercise Objectives";
    case "addToHCPCAudit":
      return "Audit tagged";
    case "draft":
      return "Draft";
    default:
      return key;
  }
}

export function getMappedEvidenceField(key: string, value: any): any {
  if (key === "date" || key === "activityDate") {
    if (isValid(new Date(value))) {
      return format(new Date(value), "dd-MM-yyyy");
    }

    return "";
  }

  if (typeof value === "string") {
    if (key === "numberOfHours") {
      return moment.duration(value, "hour").format("hh:mm", { trim: false });
    }

    return escapeCSV(value);
  }

  if (key === "draft") {
    return value ? "1" : "0";
  }

  if (typeof value === "object") {
    if (!value) {
      return "";
    }

    if (Array.isArray(value)) {
      if ("tag" in value[0]) {
        return escapeCSV(value.join(";"));
      }
    }

    if ("type" in value) {
      return escapeCSV(value.type);
    }
  }

  if (typeof value === "undefined") {
    return "";
  }
}

export function generateCSV(allEvidence: IEvidence[], rows?: string[]): string {
  let csv = "";

  if (rows && rows.length > 0) {
    csv = csv + rows.map((item) => getEvidenceFieldHeader(item)).join(",") + "\n";

    for (let i = 0; i < allEvidence.length; i++) {
      const evidence = structuredClone(allEvidence[i]);
      evidence.title = evidence.title || evidence.evidenceJSON?.Title;

      for (let j = 0; j < rows.length; j++) {
        csv = csv + getMappedEvidenceField(rows[j], evidence[`${rows[j]}` as keyof IEvidence]);

        if (j < rows.length - 1) {
          csv = csv + ",";
        }
      }

      csv = csv + "\n";
    }
  }

  return csv;
}

export function generateRadiologyAuditsCSV(params: {
  allEvidence: IEvidence[];
  evidenceDefinition: EvidenceDefinition;
}): string {
  const evidenceParts =
    params.evidenceDefinition["Evidence parts"]?.filter((item) => item["Field type"] !== "Banner") ?? [];
  const headers = evidenceParts.map((item) => item["Display Title"] ?? item.Name) ?? [];
  const keys = evidenceParts.map((item) => item.Name) ?? [];
  const fieldTypes = evidenceParts?.map((item) => item["Field type"]).filter((item) => item !== undefined) ?? [];

  if (headers.length === 0) {
    return "";
  }

  // Helper function to escape CSV values

  // Build CSV rows using array methods
  const csvRows = [
    headers.map((h) => escapeCSV(h)).join(","), // Header row
    ...params.allEvidence.map((evidence) =>
      headers
        .map((header, index) => {
          return escapeCSV(getEvidencePartValue(evidence, header, keys, fieldTypes as string[], index));
        })
        .join(",")
    ),
  ];

  return csvRows.join("\n");
}

function getEvidencePartValue(
  evidence: IEvidence,
  header: string,
  keys: string[],
  fieldTypes: string[],
  index: number
): any {
  const value = evidence.evidenceJSON[keys[index]];
  const fieldType = fieldTypes[index];

  // TODO: Extend to other Evidence Part Field Types
  if (fieldType === FieldType.TextBox && typeof value === "string") {
    return escapeCSV(stripHtmlTags(value));
  }

  if (fieldType === FieldType.Date) {
    if (isValid(new Date(value))) {
      return format(new Date(value), "dd-MM-yyyy");
    }

    return "";
  }

  if (fieldType === FieldType.RadioYesNoWithComments) {
    if (!value) {
      return "";
    }

    const radioValue: string = value.radioValue;
    const comments: string = value.textValue;

    if (radioValue.length > 0 && comments.length > 0) {
      return `${escapeCSV(radioValue)} - ${escapeCSV(stripHtmlTags(comments))}`;
    }

    if (radioValue.length > 0) {
      return escapeCSV(radioValue);
    }

    if (comments.length > 0) {
      return escapeCSV(stripHtmlTags(comments));
    }

    return "";
  }

  if (fieldType === FieldType.LOsMultiSelectForNQP) {
    if (!value) {
      return "";
    }

    const selectedOutcomes = value.selectedOutcomes;

    if (selectedOutcomes.length > 0) {
      return selectedOutcomes.map((outcome: any) => escapeCSV(outcome.name)).join(" ; ");
    }
  }

  if (fieldType === FieldType.SkillsMultiSelect) {
    if (!value) {
      return "";
    }

    return value.map((outcome: SkillsMultiSelectSelectedValue) => escapeCSV(outcome.skillName)).join(" ; ");
  }

  return escapeCSV(value);
}

export function stripHtmlTags(html: string): string {
  return html
    .replace(/&nbsp;/gm, " ")
    .replace(/<\/h2/gm, "\n\n</h2")
    .replace(/<\/span/gm, " </span")
    .replace(/<\/sup/gm, " </sup")
    .replace(/<br>/gm, "\n")
    .replace(/&#149;/gm, "•")
    .replace(/\n{3,}/gm, "\n")
    .replace(/\n{2,}/gm, "\n")
    .replace(/\s{3,}/gm, "\n    ")
    .replace(/<!--[\s\S]*?-->/gm, "")
    .replace(/&#145;/gm, "'")
    .replace(/&#146;/gm, "'")
    .replace(/&#147;/gm, '"')
    .replace(/&#148;/gm, '"')
    .replace(/&#151;/gm, "-")
    .replace(/<a[\s]+([^>]+)>((?:.(?!<\/a>))*.)<\/a>/gm, "")
    .replace(/<[^>]*>?/gm, "");
}

export function getDownloadSelectionText(filters: EvidenceSearchFilter[], evidence: IEvidence[]): string {
  if (!filters && !evidence) return "";

  const groupedFilters: { [type: string]: string[] } = filters.reduce((acc: any, filter) => {
    if (!acc[filter.type]) {
      acc[filter.type] = [];
    }
    acc[filter.type].push(filter.option);
    return acc;
  }, {});

  const array: string[] = [];

  if (groupedFilters["General"]) {
    array.push(...groupedFilters["General"]);
  }

  if (evidence.length > 0) {
    array.push(`${evidence.length} evidence${evidence.length > 1 ? "s" : ""}`);
  }

  if (groupedFilters["CPD Activity"]) {
    const length = groupedFilters["CPD Activity"].length;
    array.push(`${length} activity type${length > 1 ? "s" : ""}`);
  }

  if (groupedFilters["Program Modules"]) {
    const length = groupedFilters["Program Modules"].length;
    array.push(`${length} program module${length > 1 ? "s" : ""}`);
  }

  if (groupedFilters["NOS"]) {
    const length = groupedFilters["NOS"].length;
    array.push(`${length} NOS tag${length > 1 ? "s" : ""}`);
  }

  if (groupedFilters["Custom Tags"]) {
    const length = groupedFilters["Custom Tags"].length;
    array.push(`${length} custom tag${length > 1 ? "s" : ""}`);
  }

  if (groupedFilters["Date Range"]) {
    array.push(
      ...groupedFilters["Date Range"].map((item) => {
        switch (item) {
          case "3 months":
            return "Last 3 months";
          case "Last 6 months":
            return "6 months";
          case "1 year":
            return "Last year";
          case "2 years":
            return "Last 2 years";
          default:
            return "";
        }
      })
    );
  }

  return array.join(", ");
}

export function mapDeepLinkObjectToEvidenceJSON(value: any, evidencePart: EvidencePart): any {
  switch (evidencePart["Field type"]) {
    case "Date":
      return value ? new Date(value) : new Date();
    case "MultiLine text":
    case "SingleLine text":
      return value;
    case "Number":
      return `${Number(value ?? 0)}`;
    case "TextBox":
      return value;
    case "Attachments":
      if (value && value.pdf) {
        const attachment = {
          content: value.pdf,
          size: value.size,
          name: value.certificateFileName,
          type: "application/pdf",
          lastModified: new Date().getTime(),
        };

        // console.log("Attachment", attachment);
        return {
          attachments: [attachment],
          attachmentsToAdd: [attachment],
        };
      }
      return undefined;
    default:
      return value;
  }
}

export function getEvidenceSkillKey(evidence: IEvidence): string | undefined {
  const evidenceJSON = evidence.evidenceJSON;

  if (evidenceJSON) {
    return evidenceJSON["Skills Used"];
  }

  return undefined;
}

export function getEvidenceSkillTypeKey(evidence: IEvidence): string | undefined {
  const evidenceJSON = evidence.evidenceJSON;

  if (evidenceJSON) {
    const keys = Object.keys(evidenceJSON);
    const skillKey = getEvidenceSkillKey(evidence);
    const skillTypeKey = keys.find((key) => key.includes(" Type"));

    if (skillKey && skillTypeKey) {
      return evidenceJSON[skillTypeKey];
    }
  }

  return undefined;
}

export function getEvidenceSkillsSelect(evidence: IEvidence): string | SkillsMultiSelectSelectedValue[] | undefined {
  const skillsSelect = evidence.evidenceJSON?.SkillsSelect;

  if (typeof skillsSelect === "string") {
    const trimmed = skillsSelect.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  if (Array.isArray(skillsSelect) && skillsSelect.length > 0) {
    return skillsSelect;
  }

  return undefined;
}

export function getEvidenceSkillsSelectLabel(evidence: IEvidence): string | undefined {
  const skillsSelect = getEvidenceSkillsSelect(evidence);

  if (!skillsSelect) {
    return undefined;
  }

  if (typeof skillsSelect === "string") {
    return skillsSelect;
  }

  const skillName = skillsSelect[0]?.skillName;

  if (!skillName) {
    return undefined;
  }

  return skillsSelect.length > 1 ? `${skillName} +${skillsSelect.length - 1}` : skillName;
}

export function evidenceHasSkills(evidence: IEvidence, programID: string): boolean {
  if (ProgramUtils.isSPAPRP(programID) || ProgramUtils.isRenfrew(programID)) {
    return evidence.evidenceJSON?.SkillsSelect !== undefined;
  }

  if (ProgramUtils.isNwasARC(programID)) {
    return evidence.evidenceJSON?.["Skills Used"] !== undefined;
  }

  return false;
}

export function getSkillsCountForDisplayCard(params: {
  skillsWithinEvidence: SkillsMultiSelectSelectedValue[];
  evidenceWithSkills: IEvidence[];
  programID: string;
  currentProgramCycle: number | undefined;
}): number {
  if (ProgramUtils.isSPAPRP(params.programID)) {
    const evidenceForCycle = params.evidenceWithSkills.filter(
      (item) => item.evidenceJSON?.programInfo?.programCycle === params.currentProgramCycle
    );

    return new Set(evidenceForCycle.flatMap((item) => item.evidenceJSON.SkillsSelect)).size;
  }

  if (ProgramUtils.isNwasARC(params.programID)) {
    return new Set(params.evidenceWithSkills.map((item) => item.evidenceJSON?.["Skills Used"])).size;
  }

  return 0;
}

export function evidencePartHasNoContainer(evidencePart: EvidencePart): boolean {
  if (
    isCompMultiSelect(evidencePart) ||
    isNQPCompMultiSelect(evidencePart) ||
    isSkillsMultiSelect(evidencePart) ||
    isNOSSelect(evidencePart) ||
    isLearningOutcomesList(evidencePart)
  ) {
    return true;
  }

  return false;
}

export function getSaveEvidenceButtonText(evidenceDefinition: EvidenceDefinition | undefined): string | undefined {
  if (!evidenceDefinition) {
    return undefined;
  }

  if (evidenceDefinition.EvidenceSelectionType === "witness-simulation") {
    return "Submit for approval";
  }

  return undefined;
}

export function isSkillsSelectEvidence(evidenceDefinition: EvidenceDefinition | null): boolean {
  return evidenceDefinition?.EvidenceSelectionType === "skills";
}

export function isWPBAFormEvidence(evidenceDefinition: EvidenceDefinition | null): boolean {
  return evidenceDefinition?.EvidenceSelectionType === "wpba-supervision-form";
}

export function isWPBAFormEvidenceType(evidenceType: string | null): boolean {
  return evidenceType === "wpba-supervision-form";
}

export function evidenceHasLearningOutcomes(evidence: IEvidence): boolean {
  return (
    evidence.evidenceJSON["LearningObjectivesTickboxes"] !== undefined ||
    evidence.evidenceJSON["LearningObjectivesTickboxes Optional"] !== undefined
  );
}

export function getSkillSectionForEvidence(
  programEvidence: IEvidence,
  program: ProgramData | undefined
): string | null {
  if (!program) return null;

  const evidenceJSON = programEvidence.evidenceJSON;

  if (!evidenceJSON?.programInfo?.skillID) return null;

  const skillID = evidenceJSON?.programInfo?.skillID;

  if (!skillID) return null;

  return program.Skills?.find((s) => s.ID === skillID)?.Section ?? null;
}

export function getWitnessSimulationSkill(
  programEvidence: IEvidence,
  program: ProgramData | undefined
): ProgramSkill | undefined {
  if (!program) return undefined;

  const evidenceJSON = programEvidence.evidenceJSON;
  const witnessSimulationSkillID = evidenceJSON.programInfo?.witnessSimulationSkillID;

  if (!witnessSimulationSkillID) return undefined;

  return ProgramUtils.getAllSkillsFromRoles(program).find((skill) => skill.ID === witnessSimulationSkillID);
}

export function shouldSetApprovalStateToPending(evidenceJSON: any, allEvidenceParts: EvidencePart[]): boolean {
  const approvalTriggerPart = allEvidenceParts.find((p) => p.ApprovalTriggerValue);

  if (approvalTriggerPart) {
    const approvalTriggerValue = approvalTriggerPart.ApprovalTriggerValue;
    const evidenceValue = evidenceJSON?.[approvalTriggerPart.Name];

    if (evidenceValue !== approvalTriggerValue) {
      return true;
    }

    return false;
  }

  return false;
}
